// =============================================================================
// Nexus Pocket — mobil-first, vanilla TS, DI fetch.
// Affordance-ok: list + search + create + detail + delete.
// Bulk, táblázat, update, vicini: pocket-ből kihagyva (pocket-convention §2).
// =============================================================================

export type Nexus = {
  id: string;
  praesyn: string;
  postsyn: string;
  associatio: string;
  tractus: string | null;
  pondus: number | null;
  directus: number;
  nota: string | null;
  created_at: number;
  updated_at: number;
};

export type FetchLike = typeof fetch;
export type PocketDeps = { fetch: FetchLike; baseUrl: string; token: string };
export type PocketResult<T> = { ok: boolean; status: number; data: T | null; error: string | null };

export function makePocket(deps: PocketDeps) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<PocketResult<T>> {
    let res: Response;
    try {
      res = await deps.fetch(`${deps.baseUrl.replace(/\/$/, "")}${path}`, {
        method,
        headers: {
          ...(deps.token ? { authorization: `Bearer ${deps.token}` } : {}),
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      return { ok: false, status: 0, data: null, error: String(err) };
    }
    const text = await res.text();
    let parsed: unknown = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
    if (!res.ok) {
      const msg = parsed && typeof parsed === "object" && "error" in parsed
        ? JSON.stringify((parsed as { error: unknown }).error)
        : String(parsed ?? res.statusText);
      return { ok: false, status: res.status, data: null, error: msg };
    }
    return { ok: true, status: res.status, data: parsed as T, error: null };
  }

  return {
    list(associatio?: string, limit = 30) {
      const p = new URLSearchParams();
      if (associatio) p.set("associatio", associatio);
      p.set("limit", String(limit));
      p.set("$count", "true");
      return call<{ value: Nexus[]; "@odata.count"?: number }>("GET", `/Nexus?${p}`);
    },
    search(query: string, mode: "prefix" | "fts" | "exact" = "prefix") {
      return call<{ value: Nexus[] }>("POST", `/Nexus/findByAssociatio`, { query, mode, limit: 30 });
    },
    read(id: string) {
      return call<Nexus>("GET", `/Nexus('${encodeURIComponent(id)}')`);
    },
    create(body: Partial<Nexus>) {
      return call<Nexus>("POST", `/Nexus`, body);
    },
    remove(id: string) {
      return call<null>("DELETE", `/Nexus('${encodeURIComponent(id)}')`);
    },
    ping() {
      return call<{ service: string; version: string }>("GET", ``);
    },
  };
}

export type Pocket = ReturnType<typeof makePocket>;

// -----------------------------------------------------------------------------
// Settings
// -----------------------------------------------------------------------------
export type Settings = { baseUrl: string; token: string };
const LS_KEY = "thw8-nexus-pocket:settings";

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Settings;
  } catch { /* ignore */ }
  return { baseUrl: "http://localhost:8787/v0.1.0", token: "" };
}
export function saveSettings(s: Settings) { localStorage.setItem(LS_KEY, JSON.stringify(s)); }

// -----------------------------------------------------------------------------
// Browser attach
// -----------------------------------------------------------------------------
if (typeof document !== "undefined") {
  let settings = loadSettings();
  let pocket = makePocket({ fetch: globalThis.fetch.bind(globalThis), ...settings });

  const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
  const setStatus = (msg: string, err = false) => {
    const el = $("#status");
    el.textContent = msg;
    el.classList.toggle("err", err);
  };
  const fmtDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const dir = (d: number) => d === 1 ? "→" : "↔";

  // --- List ---
  async function doList() {
    setStatus("betöltés…");
    const q = $<HTMLInputElement>("#search-input").value.trim();
    const r = q
      ? await pocket.search(q)
      : await pocket.list();
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", true);
    const rows = r.data.value;
    const count = (r.data as { "@odata.count"?: number })["@odata.count"] ?? rows.length;
    renderCards(rows);
    setStatus(`${rows.length} / ${count} találat`);
  }

  function renderCards(rows: Nexus[]) {
    const list = $("#card-list");
    list.innerHTML = "";
    if (!rows.length) {
      list.innerHTML = `<li class="empty">— nincs találat —</li>`;
      return;
    }
    for (const row of rows) {
      const li = document.createElement("li");
      li.className = "card";
      li.dataset.id = row.id;
      li.innerHTML = `
        <div class="card-main">
          <span class="assoc">${row.associatio}</span>
          <span class="dir-badge">${dir(row.directus)}</span>
        </div>
        <div class="card-sub">${row.praesyn} → ${row.postsyn}</div>
        ${row.tractus ? `<div class="card-tag">${row.tractus}</div>` : ""}
        <div class="card-meta">${fmtDate(row.created_at)}${row.pondus != null ? ` · ${row.pondus}` : ""}</div>`;
      list.appendChild(li);
    }
  }

  // --- Detail ---
  function showDetail(row: Nexus) {
    $("#detail-assoc").textContent = row.associatio;
    $("#detail-dir").textContent = dir(row.directus);
    $("#detail-praesyn").textContent = row.praesyn;
    $("#detail-postsyn").textContent = row.postsyn;
    $("#detail-tractus").textContent = row.tractus ?? "—";
    $("#detail-pondus").textContent = row.pondus != null ? String(row.pondus) : "—";
    $("#detail-nota").textContent = row.nota ?? "—";
    $("#detail-id").textContent = row.id;
    $("#detail-created").textContent = fmtDate(row.created_at);
    $<HTMLElement>("#detail-panel").dataset.id = row.id;
    $<HTMLElement>("#detail-panel").hidden = false;
    $<HTMLElement>("#main-panel").hidden = true;
  }

  function hideDetail() {
    $<HTMLElement>("#detail-panel").hidden = true;
    $<HTMLElement>("#main-panel").hidden = false;
  }

  // --- Create ---
  async function doCreate(ev: SubmitEvent) {
    ev.preventDefault();
    const fd = new FormData(ev.target as HTMLFormElement);
    const body: Record<string, unknown> = {};
    const praesyn = String(fd.get("praesyn") ?? "").trim();
    const postsyn = String(fd.get("postsyn") ?? "").trim();
    const associatio = String(fd.get("associatio") ?? "").trim();
    const nota = String(fd.get("nota") ?? "").trim();
    if (!praesyn || !postsyn || !associatio) return setStatus("praesyn, postsyn, associatio kötelező", true);
    body.praesyn = praesyn; body.postsyn = postsyn; body.associatio = associatio;
    if (nota) body.nota = nota;
    setStatus("létrehozás…");
    const r = await pocket.create(body as never);
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", true);
    setStatus(`létrehozva: ${r.data.id}`);
    (ev.target as HTMLFormElement).reset();
    hideCreate();
    await doList();
    showDetail(r.data);
  }

  function showCreate() {
    $<HTMLElement>("#create-sheet").hidden = false;
    $<HTMLInputElement>("#new-praesyn").focus();
  }
  function hideCreate() { $<HTMLElement>("#create-sheet").hidden = true; }

  // --- Delete ---
  async function doDelete() {
    const id = $<HTMLElement>("#detail-panel").dataset.id;
    if (!id || !confirm(`Törlés: ${id}?`)) return;
    const r = await pocket.remove(id);
    if (!r.ok) return setStatus(r.error ?? "hiba", true);
    setStatus(`törölve: ${id}`);
    hideDetail();
    await doList();
  }

  // --- Settings ---
  function showSettings() { $<HTMLElement>("#settings-sheet").hidden = false; }
  function hideSettings() { $<HTMLElement>("#settings-sheet").hidden = true; }

  async function saveAndReload() {
    settings = {
      baseUrl: $<HTMLInputElement>("#set-url").value.trim(),
      token: $<HTMLInputElement>("#set-token").value,
    };
    saveSettings(settings);
    pocket = makePocket({ fetch: globalThis.fetch.bind(globalThis), ...settings });
    hideSettings();
    const p = await pocket.ping();
    setStatus(p.ok ? "backend OK" : `nem elérhető: ${p.error}`, !p.ok);
    if (p.ok) await doList();
  }

  // --- Events ---
  $("#search-input").addEventListener("input", () => void doList());
  $("#refresh-btn").addEventListener("click", () => void doList());
  $("#add-btn").addEventListener("click", showCreate);
  $("#create-cancel").addEventListener("click", hideCreate);
  $("#create-form").addEventListener("submit", (ev) => void doCreate(ev as SubmitEvent));
  $("#detail-back").addEventListener("click", hideDetail);
  $("#detail-delete").addEventListener("click", () => void doDelete());
  $("#settings-btn").addEventListener("click", () => {
    $<HTMLInputElement>("#set-url").value = settings.baseUrl;
    $<HTMLInputElement>("#set-token").value = settings.token;
    showSettings();
  });
  $("#settings-cancel").addEventListener("click", hideSettings);
  $("#settings-save").addEventListener("click", () => void saveAndReload());
  $("#card-list").addEventListener("click", async (ev) => {
    const card = (ev.target as HTMLElement).closest(".card") as HTMLElement | null;
    if (!card?.dataset.id) return;
    setStatus("betöltés…");
    const r = await pocket.read(card.dataset.id);
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", true);
    showDetail(r.data);
    setStatus("");
  });

  if (settings.token && settings.baseUrl) void doList();
  else setStatus("⚙️ Konfiguráld a beállításokat (jobb felül)");
}
